// Prompt 0470 M9 — Tab "Roles de gobernanza" en AdminPuestoPanel.
// Reciprocidad con el panel de Tipo de Rol de Gobernanza: lista los roles
// asignados al puesto + permite añadir/quitar desde este lado.
// Los roles de sistema (esPorProceso=true) se listan read-only porque se
// gestionan desde la ficha del proceso.

import type React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  fetchRolesGobernanza,
  fetchTiposRolGobernanza,
  crearRolGobernanza,
  desactivarRolGobernanza,
  type RolGobernanzaAdmin,
  type TipoRolGobernanzaAdmin,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { LoadingSpinner } from '../../../../../../shared/components/LoadingSpinner';

interface RolesGobernanzaTabProps {
  puestoId: string;
  onChanged?: () => void;
}

export function RolesGobernanzaTab({ puestoId, onChanged }: RolesGobernanzaTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const panel = ((adm ?? {}) as Record<string, unknown>).puestoPanel as
    | Record<string, string>
    | undefined;
  const lbl = panel ?? {};

  const [rolesDelPuesto, setRolesDelPuesto] = useState<RolGobernanzaAdmin[]>([]);
  const [tiposCatalogo, setTiposCatalogo] = useState<TipoRolGobernanzaAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTipoId, setSelectedTipoId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesRes, tiposRes] = await Promise.all([
        fetchRolesGobernanza(),
        fetchTiposRolGobernanza(),
      ]);
      const roles = (rolesRes.data ?? []).filter(
        (r) => r.puestoId === puestoId || r.puesto?.id === puestoId,
      );
      setRolesDelPuesto(roles);
      setTiposCatalogo(tiposRes.data ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [puestoId]);

  useEffect(() => {
    load();
  }, [load]);

  const tipoById = new Map(tiposCatalogo.map((tipo) => [tipo.id, tipo]));
  const tiposAsignados = new Set(rolesDelPuesto.map((r) => r.tipoRolId));
  const tiposDisponibles = tiposCatalogo.filter(
    (tipo) => tipo.activo && !tipo.esPorProceso && !tiposAsignados.has(tipo.id),
  );

  const asignar = async () => {
    if (!selectedTipoId) return;
    setSaving(true);
    setError('');
    try {
      await crearRolGobernanza({ puestoId, tipoRolId: selectedTipoId });
      setSelectedTipoId('');
      await load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al asignar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const desasignar = async (rolId: string) => {
    if (!window.confirm(lbl.confirmDesasignarRol ?? 'Quitar este rol del puesto?')) return;
    setSaving(true);
    setError('');
    try {
      await desactivarRolGobernanza(rolId);
      await load();
      onChanged?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al desasignar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="sm" muted />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          padding: '10px 14px',
          background: '#F0F9FF',
          color: '#075985',
          borderRadius: 8,
          fontSize: 12,
          border: '1px solid #BAE6FD',
        }}
      >
        {lbl.rolesGobernanzaInfo ??
          'Roles de gobernanza asignados a este puesto. Los roles por proceso (Representante, Direccion responsable) se gestionan desde la ficha del proceso.'}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '10px 12px',
          background: '#F9FAFB',
          borderRadius: 8,
        }}
      >
        <select
          value={selectedTipoId}
          onChange={(e) => setSelectedTipoId(e.target.value)}
          style={{ flex: 1, ...inputStyle }}
        >
          <option value="">{lbl.seleccionarRol ?? 'Selecciona un tipo de rol'}</option>
          {tiposDisponibles.map((tipo) => (
            <option key={tipo.id} value={tipo.id}>
              {tipo.nombre}
              {tipo.esSistema ? ` (${lbl.sistema ?? 'Sistema'})` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={asignar}
          disabled={!selectedTipoId || saving}
          style={{
            padding: '8px 16px',
            background: !selectedTipoId || saving ? '#9CA3AF' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: !selectedTipoId || saving ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + {lbl.asignarRol ?? 'Asignar rol'}
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 14px',
            background: '#FEF2F2',
            color: '#B91C1C',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #FECACA',
          }}
        >
          {error}
        </div>
      )}

      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#374151' }}>
          {lbl.rolesActivos ?? 'Roles activos'} ({rolesDelPuesto.length})
        </h4>
        {rolesDelPuesto.length === 0 ? (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: '#6B7280',
              background: '#F9FAFB',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {lbl.sinRolesGobernanza ?? 'Sin roles de gobernanza asignados'}
          </div>
        ) : (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
            {rolesDelPuesto.map((r) => {
              const tipo = tipoById.get(r.tipoRolId);
              const nombreTipo =
                tipo?.nombre ?? r.tipoRolDefinicion?.nombre ?? r.nombrePersonalizado ?? 'Sin tipo';
              const esPorProceso = tipo?.esPorProceso ?? r.tipoRolDefinicion?.esPorProceso ?? false;
              return (
                <div
                  key={r.id}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 13,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0F172A' }}>
                      {nombreTipo}
                      {tipo?.esSistema && (
                        <span
                          style={{
                            marginLeft: 8,
                            padding: '2px 8px',
                            background: '#EEF2FF',
                            color: '#4338CA',
                            fontSize: 10,
                            fontWeight: 600,
                            borderRadius: 4,
                            letterSpacing: 0.3,
                          }}
                        >
                          {lbl.badgeSistema ?? 'SISTEMA'}
                        </span>
                      )}
                    </div>
                    {r.ambito && (
                      <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{r.ambito}</div>
                    )}
                  </div>
                  {esPorProceso ? (
                    <span style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>
                      {lbl.gestionadoPorProceso ?? 'Gestionado desde el proceso'}
                    </span>
                  ) : (
                    <button
                      onClick={() => desasignar(r.id)}
                      disabled={saving}
                      style={{
                        background: 'none',
                        border: '1px solid #FCA5A5',
                        color: '#DC2626',
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 11,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      ✕ {lbl.quitar ?? 'Quitar'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};
